import React, { useState, useEffect } from 'react';
import { UsersIcon, CalendarIcon, MessageCircleIcon, CheckIcon, XIcon } from './icons/Icons';
import api from '../services/api';
import './CommentSection.css';

const CommentSection = ({ postId }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPopup, setShowPopup] = useState(false);
  const [formData, setFormData] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      const response = await api.get(`/comments/post/${postId}`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post(`/comments/${postId}`, formData);
      setShowPopup(true);
      setFormData({ author_name: '', author_email: '', content: '' });
    } catch (error) {
      const errorMsg = error.response?.data?.errors?.[0]?.msg || 
                       error.response?.data?.error || 
                       'Failed to submit comment';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="comment-section">
      {/* Success Popup */}
      {showPopup && (
        <div className="comment-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="comment-popup" onClick={(e) => e.stopPropagation()}>
            <button className="comment-popup-close" onClick={() => setShowPopup(false)}>
              <XIcon size={20} />
            </button>
            <div className="comment-popup-icon">
              <CheckIcon size={32} />
            </div>
            <h4>Comment Submitted!</h4>
            <p>Thank you for your comment. It has been submitted successfully and is awaiting admin approval. It will appear once reviewed.</p>
            <button className="btn btn-primary" onClick={() => setShowPopup(false)}>
              Got it
            </button>
          </div>
        </div>
      )}

      <h3 className="comment-section-title">
        <MessageCircleIcon size={24} />
        Comments ({comments.length})
      </h3>

      {/* Comment Form */}
      <div className="comment-form-wrapper">
        <h4>Leave a Comment</h4>
        
        {message.text && (
          <div className={`alert alert-${message.type}`}>{message.text}</div>
        )}

        <form onSubmit={handleSubmit} className="comment-form">
          <div className="comment-form-row">
            <div className="form-group">
              <label htmlFor="author_name" className="form-label">Name *</label>
              <input
                type="text"
                id="author_name"
                name="author_name"
                value={formData.author_name}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Your name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="author_email" className="form-label">Email *</label>
              <input
                type="email"
                id="author_email"
                name="author_email"
                value={formData.author_email}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Your email (not published)"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="content" className="form-label">Comment *</label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              className="form-input form-textarea"
              required
              rows={4}
              placeholder="Write your comment..."
              minLength={10}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Post Comment'}
          </button>
        </form>
      </div>

      {/* Comments List */}
      <div className="comments-list">
        {loading ? (
          <p className="comments-loading">Loading comments...</p>
        ) : comments.length === 0 ? (
          <p className="comments-empty">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="comment-item">
              <div className="comment-avatar">
                <UsersIcon size={24} />
              </div>
              <div className="comment-content">
                <div className="comment-header">
                  <span className="comment-author">{comment.author_name}</span>
                  <span className="comment-date">
                    <CalendarIcon size={14} />
                    {formatDate(comment.created_at)}
                  </span>
                </div>
                <p className="comment-text">{comment.content}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
